/**
 * Centralized coordinate resolution for supplier news: use stored lat/lng or derive from region.
 * Used by: Dashboard map, Excel import, seed script (via JSON), optional backfill.
 */

import regionMap from './regionCoordinatesMap.json'

const COORDS_MAP: Record<string, [number, number]> = regionMap as unknown as Record<string, [number, number]>

export interface GetCoordinatesInput {
  lat?: number | null
  lng?: number | null
  region?: string | null
  /** Fallback when news.region is empty (e.g. Master Database "Not classified"). */
  supplierLocation?: string | null
  kenvueRegion?: string | null
}

export interface GetCoordinatesOptions {
  /** Add small deterministic jitter so multiple items in same place don't overlap. */
  jitter?: boolean
  /** Used with jitter for deterministic offset (e.g. news id). */
  id?: string
}

const JITTER_SCALE = 0.2

const PLACEHOLDER_LOCATIONS = new Set([
  'not classified',
  'not available',
  'unavailable',
  'na',
  'n/a',
  'tbd',
  'global',
  '',
])

/** Simple deterministic hash of string to 0..1 for jitter. */
function hashTo01(s: string): number {
  let h = 0
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i)
    h |= 0
  }
  return ((h >>> 0) % 10000) / 10000
}

function applyJitter(lat: number, lng: number, id: string): { lat: number; lng: number } {
  return {
    lat: lat + (hashTo01(id) - 0.5) * 2 * JITTER_SCALE,
    lng: lng + (hashTo01(id + '2') - 0.5) * 2 * JITTER_SCALE,
  }
}

function isPlaceholder(s: string | null | undefined): boolean {
  if (s == null) return true
  return PLACEHOLDER_LOCATIONS.has(s.trim().toLowerCase())
}

/**
 * Clean a location string: trim, lowercase, drop parenthetical notes, take first slash/pipe segment.
 */
export function normalizeRegion(region: string | null | undefined): string | null {
  if (region == null || typeof region !== 'string') return null
  let s = region.trim()
  if (!s) return null
  s = s.replace(/\([^)]*\)/g, ' ').replace(/\s+/g, ' ').trim()
  if (!s) return null
  const lower = s.toLowerCase()
  if (isPlaceholder(lower)) return null
  const first = lower.split(/\s*[\/|]\s*/)[0].trim()
  return first || lower
}

/** Candidate lookup keys from a free-text location (most specific first). */
export function locationLookupKeys(raw: string | null | undefined): string[] {
  const base = normalizeRegion(raw)
  if (!base) return []
  const keys: string[] = []
  const push = (k: string | null | undefined) => {
    if (!k) return
    const t = k.trim().toLowerCase()
    if (!t || isPlaceholder(t) || keys.includes(t)) return
    keys.push(t)
  }

  push(base)

  // City, State, Country → also try "city, state", state, country, city
  const parts = base.split(/\s*,\s*/).map((p) => p.trim()).filter(Boolean)
  if (parts.length >= 2) {
    push(parts.slice(0, 2).join(', '))
    for (let i = parts.length - 1; i >= 0; i--) push(parts[i])
  }

  // "new brunswick, new jersey, usa" → also "new jersey, usa"
  if (parts.length >= 3) {
    push(parts.slice(1).join(', '))
  }

  return keys
}

function lookupCoords(raw: string | null | undefined): [number, number] | null {
  for (const key of locationLookupKeys(raw)) {
    const coords = COORDS_MAP[key]
    if (coords && Array.isArray(coords) && coords.length >= 2) {
      return [coords[0], coords[1]]
    }
  }
  return null
}

/**
 * Resolve coordinates for a news item: use stored lat/lng if valid, else lookup by region
 * or supplier location / kenvue region fallbacks.
 */
export function getCoordinatesForNews(
  item: GetCoordinatesInput,
  options?: GetCoordinatesOptions
): { lat: number; lng: number } | null {
  const { jitter = false, id = '' } = options ?? {}

  const hasStored =
    item.lat != null &&
    item.lng != null &&
    Number.isFinite(Number(item.lat)) &&
    Number.isFinite(Number(item.lng))
  if (hasStored) {
    let lat = Number(item.lat)
    let lng = Number(item.lng)
    if (jitter && id) {
      ;({ lat, lng } = applyJitter(lat, lng, id))
    }
    return { lat, lng }
  }

  const candidates = [item.region, item.supplierLocation, item.kenvueRegion]
  for (const c of candidates) {
    const found = lookupCoords(c)
    if (found) {
      let [lat, lng] = found
      if (jitter && id) {
        ;({ lat, lng } = applyJitter(lat, lng, id))
      }
      return { lat, lng }
    }
  }
  return null
}

/** Prefer a displayable region label for UI / backfill (skip placeholders). */
export function pickDisplayRegion(
  ...candidates: Array<string | null | undefined>
): string | null {
  for (const c of candidates) {
    if (c == null) continue
    const t = c.trim()
    if (!t || isPlaceholder(t)) continue
    return t
  }
  return null
}

/** Display location for a news row: region → supplier_location → supplier location/kenvue. */
export function resolveNewsDisplayLocation(
  news: {
    region?: string | null
    supplier_location?: string | null
  },
  supplier?: {
    location?: string | null
    kenvue_region?: string | null
  } | null
): string | null {
  return pickDisplayRegion(
    news.region,
    news.supplier_location,
    supplier?.location,
    supplier?.kenvue_region
  )
}

/**
 * Resolve coords for a news row given an optional linked supplier.
 */
export function resolveNewsCoordinates(
  news: {
    id?: string
    lat?: number | null
    lng?: number | null
    region?: string | null
    supplier_location?: string | null
  },
  supplier?: {
    location?: string | null
    kenvue_region?: string | null
  } | null,
  options?: GetCoordinatesOptions
): { lat: number; lng: number } | null {
  return getCoordinatesForNews(
    {
      lat: news.lat,
      lng: news.lng,
      region: news.region,
      supplierLocation: news.supplier_location ?? supplier?.location ?? null,
      kenvueRegion: supplier?.kenvue_region ?? null,
    },
    { jitter: options?.jitter ?? false, id: options?.id ?? news.id ?? '' }
  )
}

/** Display location for a mitigation task: plant_location → supplier location/kenvue. */
export function resolveMitigationDisplayLocation(
  task: {
    plant_location?: string | null
  },
  supplier?: {
    location?: string | null
    kenvue_region?: string | null
  } | null
): string | null {
  return pickDisplayRegion(task.plant_location, supplier?.location, supplier?.kenvue_region)
}

/**
 * Resolve coords for a mitigation task from plant_location / linked supplier geography.
 */
export function resolveMitigationCoordinates(
  task: {
    id?: string
    plant_location?: string | null
  },
  supplier?: {
    location?: string | null
    kenvue_region?: string | null
  } | null,
  options?: GetCoordinatesOptions
): { lat: number; lng: number } | null {
  return getCoordinatesForNews(
    {
      region: task.plant_location ?? null,
      supplierLocation: supplier?.location ?? null,
      kenvueRegion: supplier?.kenvue_region ?? null,
    },
    { jitter: options?.jitter ?? false, id: options?.id ?? task.id ?? '' }
  )
}
